// Generated from WellnessHub APro7 infrared conclusion rules. Run scripts/sync-m60s-infrared.js.
const MAX_LIST_PARTS = 3;
/**
 * 产品规则依据：docs/infrared/rules.md §10.2.1、§10.3.2。
 * 前后变化仅看「本次区域均温 - 上次区域均温」：
 * - abs(ΔT) < 0.3℃：变化较小
 * - 0.3℃ <= abs(ΔT) < 0.6℃：存在一定变化
 * - abs(ΔT) >= 0.6℃：明显变化（UI 短文案再按正负拆成明显升高/明显降低）
 */
export function calcTempChangeLevel(delta) {
    const abs = Math.abs(Number(delta) || 0);
    if (abs < 0.3)
        return 'slight';
    if (abs < 0.6)
        return 'moderate';
    return 'obvious';
}
/**
 * 返回变化短文案的 i18n key（TOP3 / 徽章 / 热图胶囊共用）。
 * 按 §10.2.1：明显升高、明显降低、存在一定变化、变化较小。
 */
export function getTempChangeLabelKey(delta) {
    const value = Number(delta) || 0;
    if (value >= 0.6)
        return 'infrared.change.riseObvious';
    if (value <= -0.6)
        return 'infrared.change.fallObvious';
    if (Math.abs(value) >= 0.3)
        return 'infrared.change.moderate';
    return 'infrared.change.slight';
}
/**
 * 产品规则依据：docs/infrared/rules.md §10.2.3、§10.3.4。
 * 左右差异阈值保持 0.3℃ / 0.6℃ 两档边界。
 */
export function calcLrAsymmetryLevel(lrDiff) {
    const value = Math.abs(Number(lrDiff) || 0);
    if (value < 0.3)
        return 'slight';
    if (value < 0.6)
        return 'moderate';
    return 'obvious';
}
/**
 * 产品规则依据：docs/infrared/rules.md §10.3.3。
 * 注意：旧 plan.md 的基础阈值曾使用 0.8℃ / 1.5℃；rules.md 是产品给出的
 * 最新结论文案规则，部位「区域范围」现在按 0.6℃ / 1.2℃ 分档。
 */
export function calcRegionalFluctuationLevel(range) {
    const value = Math.abs(Number(range) || 0);
    if (value < 0.6)
        return 'slight';
    if (value < 1.2)
        return 'moderate';
    return 'obvious';
}
/** 根据左右均温判断偏高侧，用于 TOP3 卡片、部位详情与整体左右差异文案。 */
export function getAsymmetryDirection(leftAvg, rightAvg) {
    const diff = Number(leftAvg) - Number(rightAvg);
    if (diff > 0)
        return 'left';
    if (diff < 0)
        return 'right';
    return 'equal';
}
/**
 * docs/infrared/rules.md §10.2.3 / §10.3.4 明确「脊柱不参与左右差异评估」。
 * 优先读取数据项 format 输出的 supportsAsymmetry；兼容旧数据时再按 part.code 排除 spine。
 * 2026-07-24 产品决定暂不展示 left_hip/right_hip；是否展示臀部由 INFRARED_PARTS 控制。
 */
export function supportsAsymmetryConclusion(part) {
    var _a;
    if (!part)
        return false;
    if (typeof ((_a = part.extra) === null || _a === void 0 ? void 0 : _a.supportsAsymmetry) === 'boolean') {
        return part.extra.supportsAsymmetry;
    }
    return part.code !== 'spine';
}
function toFixed1(value) {
    return Math.abs(value).toFixed(1);
}
function partName(t, part) {
    return t(part.nameKey);
}
function directionText(t, direction) {
    if (direction === 'left')
        return t('infrared.asymmetry.leftHigher');
    if (direction === 'right')
        return t('infrared.asymmetry.rightHigher');
    return '';
}
/**
 * 产品规则中的「最多展示 3 个部位；超过 3 个时部位列表后增加‘等’」。
 * 英文沿用同一 key，避免在逻辑层硬编码中文连接符或后缀。
 */
function formatPartList(t, parts) {
    const visible = parts.slice(0, MAX_LIST_PARTS).map(part => partName(t, part));
    const suffix = parts.length > MAX_LIST_PARTS ? t('infrared.conclusion.listEtc') : '';
    return visible.join(t('infrared.conclusion.listSeparator')) + suffix;
}
function getRegionRange(part) {
    const explicit = Number(part.extra.regionTempDiff);
    if (Number.isFinite(explicit))
        return explicit;
    return Number(part.extra.regionMaxTemp) - Number(part.extra.regionMinTemp);
}
function buildChangeCandidates(parts, previousPartMap) {
    return parts
        .map(part => {
        const previous = previousPartMap[part.code];
        if (!previous)
            return null;
        const delta = Number((part.value - previous.value).toFixed(1));
        return {
            part,
            previous,
            delta,
            absDelta: Math.abs(delta),
        };
    })
        .filter(Boolean);
}
function buildAsymmetryCandidates(parts) {
    return parts
        .filter(supportsAsymmetryConclusion)
        .map(part => {
        const diff = Math.abs(Number(part.extra.leftRightTempDiff) || 0);
        return {
            part,
            diff,
            direction: getAsymmetryDirection(Number(part.extra.leftAvgTemp), Number(part.extra.rightAvgTemp)),
        };
    });
}
export function buildPartChangeText(t, part, previous) {
    if (!part || !previous)
        return '--';
    const delta = Number((part.value - previous.value).toFixed(1));
    const name = partName(t, part);
    // docs/infrared/rules.md §10.3.2：按优先级从明显升高到变化较小命中一条。
    if (delta >= 0.6) {
        return t('infrared.conclusion.partChange.riseObvious', { part: name, delta: toFixed1(delta) });
    }
    if (delta <= -0.6) {
        return t('infrared.conclusion.partChange.fallObvious', { part: name, delta: toFixed1(delta) });
    }
    if (delta >= 0.3) {
        return t('infrared.conclusion.partChange.riseModerate', { part: name, delta: toFixed1(delta) });
    }
    if (delta <= -0.3) {
        return t('infrared.conclusion.partChange.fallModerate', { part: name, delta: toFixed1(delta) });
    }
    return t('infrared.conclusion.partChange.slight', { part: name });
}
export function buildPartDistributionText(t, part) {
    if (!part)
        return '--';
    const range = getRegionRange(part);
    const name = partName(t, part);
    // docs/infrared/rules.md §10.3.3：区域范围 = 区域高值 - 区域低值。
    if (range >= 1.2) {
        return t('infrared.conclusion.partDistribution.obvious', { part: name });
    }
    if (range >= 0.6) {
        return t('infrared.conclusion.partDistribution.moderate', { part: name });
    }
    return t('infrared.conclusion.partDistribution.slight', { part: name });
}
export function buildPartAsymmetryText(t, part) {
    if (!supportsAsymmetryConclusion(part))
        return '';
    const supportedPart = part;
    const diff = Math.abs(Number(supportedPart.extra.leftRightTempDiff) || 0);
    const name = partName(t, supportedPart);
    // docs/infrared/rules.md §10.3.4：部位左右差异只展示温差，不展示左右具体均温。
    if (diff >= 0.6) {
        return t('infrared.conclusion.partAsymmetry.obvious', { part: name, diff: diff.toFixed(1) });
    }
    if (diff >= 0.3) {
        return t('infrared.conclusion.partAsymmetry.moderate', { part: name, diff: diff.toFixed(1) });
    }
    return t('infrared.conclusion.partAsymmetry.slight', { part: name });
}
export function buildOverallChangeFocusText(t, parts, previousPartMap) {
    const candidates = buildChangeCandidates(parts, previousPartMap);
    if (!candidates.length)
        return null;
    const obviousUp = candidates.filter(item => item.delta >= 0.6).sort((a, b) => b.delta - a.delta);
    const obviousDown = candidates.filter(item => item.delta <= -0.6).sort((a, b) => b.absDelta - a.absDelta);
    const moderate = candidates
        .filter(item => item.absDelta >= 0.3 && item.absDelta < 0.6)
        .sort((a, b) => b.absDelta - a.absDelta);
    // docs/infrared/rules.md §10.2.1：整体变化重点按 1~7 优先级唯一命中。
    if (obviousUp.length && obviousDown.length) {
        return t('infrared.overall.changeFocus.mixed', {
            upParts: formatPartList(t, obviousUp.map(item => item.part)),
            downParts: formatPartList(t, obviousDown.map(item => item.part)),
        });
    }
    if (obviousUp.length >= 2) {
        return t('infrared.overall.changeFocus.multiRise', {
            parts: formatPartList(t, obviousUp.map(item => item.part)),
        });
    }
    if (obviousDown.length >= 2) {
        return t('infrared.overall.changeFocus.multiFall', {
            parts: formatPartList(t, obviousDown.map(item => item.part)),
        });
    }
    if (obviousUp.length === 1) {
        return t('infrared.overall.changeFocus.singleRise', {
            part: partName(t, obviousUp[0].part),
            delta: toFixed1(obviousUp[0].delta),
        });
    }
    if (obviousDown.length === 1) {
        return t('infrared.overall.changeFocus.singleFall', {
            part: partName(t, obviousDown[0].part),
            delta: toFixed1(obviousDown[0].delta),
        });
    }
    if (moderate.length) {
        return t('infrared.overall.changeFocus.moderate', {
            parts: formatPartList(t, moderate.map(item => item.part)),
        });
    }
    return t('infrared.overall.changeFocus.slight');
}
export function buildOverallDistributionText(t, parts) {
    const values = parts.map(part => Number(part.value)).filter(Number.isFinite);
    if (!values.length)
        return '--';
    const distributionDiff = Math.max(...values) - Math.min(...values);
    // docs/infrared/rules.md §10.2.2：整体分布评估「各重点 ROI 区域均温最大 - 最小」。
    if (distributionDiff >= 1.2) {
        return t('infrared.overall.distribution.obvious');
    }
    if (distributionDiff >= 0.6) {
        return t('infrared.overall.distribution.moderate');
    }
    return t('infrared.overall.distribution.slight');
}
export function buildOverallAsymmetryText(t, parts) {
    const candidates = buildAsymmetryCandidates(parts);
    if (!candidates.length)
        return '--';
    const obvious = candidates.filter(item => item.diff >= 0.6).sort((a, b) => b.diff - a.diff);
    const moderate = candidates.filter(item => item.diff >= 0.3 && item.diff < 0.6).sort((a, b) => b.diff - a.diff);
    // docs/infrared/rules.md §10.2.3：整体左右差异按 1~5 优先级唯一命中。
    if (obvious.length >= 2) {
        return t('infrared.overall.asymmetry.multiObvious', {
            parts: formatPartList(t, obvious.map(item => item.part)),
        });
    }
    if (obvious.length === 1) {
        return t('infrared.overall.asymmetry.singleObvious', {
            part: partName(t, obvious[0].part),
            direction: directionText(t, obvious[0].direction),
            diff: obvious[0].diff.toFixed(1),
        });
    }
    if (moderate.length >= 2) {
        return t('infrared.overall.asymmetry.multiModerate', {
            parts: formatPartList(t, moderate.map(item => item.part)),
        });
    }
    if (moderate.length === 1) {
        return t('infrared.overall.asymmetry.singleModerate', {
            part: partName(t, moderate[0].part),
            direction: directionText(t, moderate[0].direction),
            diff: moderate[0].diff.toFixed(1),
        });
    }
    return t('infrared.overall.asymmetry.slight');
}
